import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import { resolveVerticalStack } from "@yjsgames/phaser-ui/panel-content";
import type { TextDirection } from "@yjsgames/i18n";
import { computeLabelButtonRow } from "./tabRowLayout.js";
import shopSpec from "../data/layouts/shop.json";
import { CRAZY_GAMES_INLINE_BANNER_SLOT } from "../ads/CrazyGamesBannerSlot.js";

/** 상품 행 수 기본값 — 골드 상자 / 다이아 상자 / 광고 제거. 광고제거 게이트 시 2(rowCount 인자). */
const ROW_COUNT = 3;
/** 제목 아래 팁 캐러셀 영역 — 높이 40, 위 간격 6 (스탯/합성 탭 동일). 그리드가 그만큼 아래로 밀린다. */
const TIP_H = 40;
const TIP_GAP = 6;
/** 스크롤바 차지 폭 — 카드가 스크롤바를 덮지 않게 우측 여백 확보(일일상점/환생과 동일 양식). */
const SCROLLBAR_GUTTER = 12;

/**
 * 상점 상품 1칸(카드)의 레이아웃 좌표 집합 — `computeShopLayout`의 `cards` 배열 원소 타입.
 * 카드가 ScrollableList 내부에 들어가므로 **스크롤 로컬 좌표**로 제공된다 (일일상점 셀과 동형 grid 카드).
 */
export interface ShopCardLayout {
  /** 카드 배경 패널 rect (origin 0,0 — top-left). */
  readonly bg: { x: number; y: number; width: number; height: number };
  /** 상품 아이콘 (카드 상단 중앙, origin 0.5). */
  readonly icon: { x: number; y: number };
  /** 상품 설명 (아이콘 아래 중앙, origin 0.5/0, align center). */
  readonly desc: { x: number; y: number; wrapWidth: number };
  /** 구매 버튼 (카드 하단 중앙) + 폭(카드 폭 가변). */
  readonly button: { x: number; y: number; width: number };
  /** 확률(?) 버튼 (카드 우상단, RTL 좌상단) — 골드 카드만 렌더, 그 외 무시. */
  readonly help: { x: number; y: number };
}

export interface ShopLayout {
  /** 하단 결과 텍스트 origin x. */
  readonly textOriginX: number;
  /** 팁 캐러셀 영역 (전체폭, 스크롤 위 고정) — TipCarouselView 배치 기준. */
  readonly tip: Rect;
  /** 스크롤 영역 rect (팁 아래 ~ 결과 위, 세로 스택 fill). */
  readonly scroll: Rect;
  /** 스크롤 내부 상품 카드 [골드1, 골드10, 다이아, (광고제거), 광고다이아…] (로컬 좌표, 2열 grid). */
  readonly cards: readonly ShopCardLayout[];
  /** CrazyGames 웹 inline responsive 배너 슬롯 (스크롤 콘텐츠 로컬 좌표). */
  readonly bannerSlot: { x: number; y: number; width: number; height: number; gapY: number };
  /** 스크롤 콘텐츠 총 높이 (행 수 × 카드 높이). */
  readonly contentHeight: number;
  /** 하단 고정 최근 개봉 결과 텍스트 (스크롤 영역 밖). */
  readonly result: { x: number; y: number };
}

export interface ShopLayoutOptions {
  readonly inlineBannerEnabled?: boolean;
}

/**
 * 상점 탭 레이아웃 좌표 계산 — 팁(상단 고정) + **스크롤 2열 grid 카드** + 결과(하단 고정).
 * 일일상점(computeDailyShopLayout) 카드 grid 양식을 차용하되, 행 수가 가변(광고 다이아 N행)이라
 * 고정 2×3 대신 ScrollableList로 세로 스크롤 수용한다(카드 높이 고정, 행 수만큼 contentHeight 확장).
 *
 * @param content     컨텐츠 영역 Rect.
 * @param dir         텍스트 방향. RTL 시 열 순서(좌→우)·확률 버튼 코너가 미러된다.
 * @param cardCount   표시할 상품 카드 수(기본 3). 광고 다이아 포함 시 호출자가 합산해 전달.
 * @param panelBottom 탭 콘텐츠 패널 하단 — 결과 텍스트/스크롤 영역 높이 기준.
 */
export function computeShopLayout(
  content: Rect,
  dir: TextDirection,
  cardCount: number = ROW_COUNT,
  panelBottom: number = content.y,
  options: ShopLayoutOptions = {},
): ShopLayout {
  const head = computeLabelButtonRow(content.x, content.width, 0, dir);
  const TOP_PAD = 4;
  // 결과 텍스트는 패널 하단 고정, 스크롤은 팁 아래 ~ 결과 위(fill).
  const resultY = panelBottom - shopSpec.resultBottomInset;
  const stackBottom = resultY - shopSpec.listBottomGap;
  const stackTop = content.y + TOP_PAD;
  const stack = resolveVerticalStack(
    {
      children: [
        { id: "tip", height: TIP_H },
        { id: "scroll", fill: true, gapTop: TIP_GAP },
      ],
    },
    { x: content.x, y: stackTop, width: content.width, height: stackBottom - stackTop },
  );
  const tipRect = stack.tip!;
  const scrollRect = stack.scroll!;

  const cols = shopSpec.cols;
  const cardH = shopSpec.cardHeight;
  const gutter = shopSpec.cardGutter;
  const pad = shopSpec.cardPad;
  const help = shopSpec.helpSize;
  // 카드는 스크롤 로컬 좌표 — 가용 폭(스크롤바 거터 제외)을 cols로 분할, 열 사이 gutter.
  const usableW = content.width - SCROLLBAR_GUTTER;
  const cardW = Math.floor((usableW - gutter * (cols - 1)) / cols);
  const inlineBannerEnabled = options.inlineBannerEnabled ?? true;

  const cards: ShopCardLayout[] = [];
  const bannerWidth = inlineBannerEnabled
    ? Math.min(scrollRect.width, Math.max(usableW, CRAZY_GAMES_INLINE_BANNER_SLOT.minWidth))
    : 0;
  const bannerSlot = {
    x: Math.max(0, Math.floor((scrollRect.width - bannerWidth) / 2)),
    y: cardH + (inlineBannerEnabled ? CRAZY_GAMES_INLINE_BANNER_SLOT.gapY : 0),
    width: bannerWidth,
    height: inlineBannerEnabled ? CRAZY_GAMES_INLINE_BANNER_SLOT.height : 0,
    gapY: inlineBannerEnabled ? CRAZY_GAMES_INLINE_BANNER_SLOT.gapY : 0,
  };
  const bannerOffset = bannerSlot.height + bannerSlot.gapY * 2;
  for (let i = 0; i < cardCount; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const mcol = dir === "rtl" ? cols - 1 - col : col;
    const cardLeft = mcol * (cardW + gutter);
    const cardTop = row * cardH + (row >= 1 ? bannerOffset : 0);
    const cx = cardLeft + cardW / 2;
    cards.push({
      bg: { x: cardLeft, y: cardTop + pad / 2, width: cardW, height: cardH - pad },
      icon: { x: cx, y: cardTop + pad / 2 + shopSpec.iconTop },
      desc: { x: cx, y: cardTop + pad / 2 + shopSpec.descTop, wrapWidth: cardW - pad * 2 },
      button: {
        x: cx,
        y: cardTop + cardH - pad / 2 - shopSpec.buyBtnBottomMargin - shopSpec.buyBtnHeight / 2,
        width: cardW - shopSpec.buyBtnWidthInset,
      },
      help: {
        x:
          dir === "rtl"
            ? cardLeft + pad + shopSpec.helpInset + help / 2
            : cardLeft + cardW - pad - shopSpec.helpInset - help / 2,
        y: cardTop + pad / 2 + shopSpec.helpInset + help / 2,
      },
    });
  }

  return {
    textOriginX: head.textOriginX,
    tip: { x: content.x, y: tipRect.y, width: content.width, height: tipRect.height },
    scroll: scrollRect,
    cards,
    bannerSlot,
    contentHeight: Math.ceil(cardCount / cols) * cardH + bannerOffset,
    result: { x: head.textX, y: resultY },
  };
}

/** 확률(?) 버튼 폭 — ShopTabView widthOverride 공용 SSoT (뷰가 import). */
export const SHOP_HELP_SIZE = shopSpec.helpSize;
/** 구매 버튼 높이 — ShopTabView heightOverride 공용 SSoT. */
export const SHOP_BUTTON_HEIGHT = shopSpec.buyBtnHeight;
